/**
 * Canonical application stages, in progression order. Shared by the frontend
 * and the edge functions so neither side has to restate the sequence.
 */
export const APPLICATION_STAGES = [
  "basic-details",
  "pending",
  "driver-onroad-test",
  "documents",
  "screening",
  "orientation",
  "orientation-quiz",
  "personal-details",
  "trial",
  "contract",
  "vehicle-collection",
  "complete",
  "rejected",
  "hold",
] as const

export type Stage = (typeof APPLICATION_STAGES)[number]

/** Exits available from anywhere; they sit outside the progression order. */
const TERMINAL_STAGES: Stage[] = ["rejected", "hold"]

/**
 * Whether `candidate` sits at or before `stage` in the progression. Terminal
 * exits have no position, so they are never "at or before" anything.
 */
export function isAtOrBefore(candidate: string, stage: Stage): boolean {
  if (TERMINAL_STAGES.includes(candidate as Stage)) {
    return false
  }

  const candidateIndex = APPLICATION_STAGES.indexOf(candidate as Stage)
  if (candidateIndex === -1) {
    return false
  }

  return candidateIndex <= APPLICATION_STAGES.indexOf(stage)
}

/** Stages after `stage` in the progression, excluding the terminal exits. */
export function stagesAfter(stage: Stage): Stage[] {
  if (TERMINAL_STAGES.includes(stage)) {
    return []
  }

  return APPLICATION_STAGES.slice(APPLICATION_STAGES.indexOf(stage) + 1).filter(
    (candidate) => !TERMINAL_STAGES.includes(candidate),
  )
}
